import * as React from "react";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import useIngestionActions from "../hooks/use-ingestion-actions";
import { useFileShares } from "../hooks/use-ingestion-data";
import { EmailIngestion, EmailIngestionFormValues, FormErrors } from "../types";
import { emailFormValuesFrom } from "../utils/mappers";
import { validateEmailIngestion } from "../utils/validation";

type TextFieldName = Exclude<
  keyof EmailIngestionFormValues,
  "enabled" | "destinationFileShareId" | "destinationSftpId"
>;

const DESTINATION_NONE = "none";
const DESTINATION_CURRENT_SFTP = "sftp";
const fileShareOption = (id: number) => `share-${id}`;

const destinationValueOf = (values: EmailIngestionFormValues): string => {
  if (values.destinationFileShareId !== null) {
    return fileShareOption(values.destinationFileShareId);
  }
  return values.destinationSftpId !== null
    ? DESTINATION_CURRENT_SFTP
    : DESTINATION_NONE;
};

export interface EmailIngestionDialogProps {
  open: boolean;
  clientId: number;
  /** The ingestion to edit; omitted when creating. */
  ingestion?: EmailIngestion;
  /** `saved` is true when the ingestion was created or updated. */
  onClose: (saved: boolean) => void;
}

const EmailIngestionDialog = ({
  open,
  clientId,
  ingestion,
  onClose,
}: EmailIngestionDialogProps) => {
  const { saveEmailIngestion } = useIngestionActions();
  const { data: fileShares, error: fileSharesError } = useFileShares(open);
  const [values, setValues] = React.useState<EmailIngestionFormValues>(() =>
    emailFormValuesFrom(ingestion),
  );
  const [errors, setErrors] = React.useState<
    FormErrors<EmailIngestionFormValues>
  >({});
  const [isSaving, setIsSaving] = React.useState(false);
  const isEdit = ingestion !== undefined;
  // No endpoint lists SFTP servers, so an existing SFTP destination can only
  // be kept or replaced with a file share.
  const currentSftp = ingestion?.destinationSftp ?? null;

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setValues((previous) => ({ ...previous, [name]: value }));
  };

  const handleDestinationChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = event.target;
    setValues((previous) => ({
      ...previous,
      destinationFileShareId: value.startsWith("share-")
        ? Number(value.slice("share-".length))
        : null,
      destinationSftpId:
        value === DESTINATION_CURRENT_SFTP ? (currentSftp?.id ?? null) : null,
    }));
  };

  const handleSubmit = async () => {
    const validationErrors = validateEmailIngestion(values);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setIsSaving(true);
    const saved = await saveEmailIngestion(clientId, ingestion, values);
    setIsSaving(false);
    if (saved) {
      onClose(true);
    }
  };

  const textField = (
    name: TextFieldName,
    label: string,
    options: { required?: boolean; type?: string; helperText?: string } = {},
  ) => (
    <TextField
      key={name}
      id={`email-ingestion-${name}`}
      name={name}
      label={label}
      type={options.type}
      required={options.required}
      value={values[name]}
      onChange={handleTextChange}
      error={errors[name] !== undefined}
      helperText={errors[name] ?? options.helperText}
      autoComplete={options.type === "password" ? "new-password" : "off"}
      size="small"
      fullWidth
    />
  );

  const destinationHelperText = () => {
    if (fileSharesError) {
      return "File shares could not be loaded.";
    }
    if (
      destinationValueOf(values) === DESTINATION_CURRENT_SFTP &&
      currentSftp
    ) {
      return `Attachments are delivered to the SFTP server ${currentSftp.hostName}. SFTP destinations can only be kept or replaced with a file share here.`;
    }
    return "Where extracted attachments are delivered";
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      maxWidth="sm"
      fullWidth
      aria-labelledby="email-ingestion-dialog-title"
    >
      <DialogTitle
        id="email-ingestion-dialog-title"
        sx={{ borderBottom: 2, mb: 2, borderColor: "divider" }}
      >
        {isEdit ? "Edit Email Ingestion" : "Add Email Ingestion"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {textField("description", "Description", { required: true })}
          {textField("folder", "Mailbox folder", {
            required: true,
            helperText: "Folder in the mailbox to read messages from",
          })}
          {textField("senderMask", "Sender mask", {
            required: true,
            helperText:
              "Only mail from matching senders is processed (* for any)",
          })}
          {textField("attachmentMask", "Attachment mask", {
            required: true,
            helperText: "Only matching attachments are extracted, e.g. *.csv",
          })}
          <TextField
            id="email-ingestion-destination"
            select
            label="Destination"
            value={destinationValueOf(values)}
            onChange={handleDestinationChange}
            error={Boolean(fileSharesError)}
            helperText={destinationHelperText()}
            size="small"
            fullWidth
          >
            <MenuItem value={DESTINATION_NONE}>None</MenuItem>
            {currentSftp ? (
              <MenuItem value={DESTINATION_CURRENT_SFTP}>
                {`SFTP server ${currentSftp.hostName} (current)`}
              </MenuItem>
            ) : null}
            {(fileShares ?? []).map((share) => (
              <MenuItem key={share.id} value={fileShareOption(share.id)}>
                {`${share.description} (${share.path})`}
              </MenuItem>
            ))}
          </TextField>
          {textField("mailBoxDescription", "Mailbox description", {
            helperText: isEdit
              ? "Name of this ingestion's mailbox"
              : "Leave blank to use the existing mailbox's description",
          })}
          <FormControlLabel
            control={
              <Checkbox
                checked={values.enabled}
                onChange={(_event, checked) =>
                  setValues((previous) => ({ ...previous, enabled: checked }))
                }
              />
            }
            label="Enabled"
          />
          <Accordion
            disableGutters
            variant="outlined"
            slotProps={{ transition: { unmountOnExit: true } }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              id="email-ingestion-connection-header"
              aria-controls="email-ingestion-connection-content"
            >
              Override mailbox credentials
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  {isEdit
                    ? "Leave blank to keep the current values. Stored credentials are never shown."
                    : "Leave blank to reuse the existing credentials from the newest mailbox."}
                </Typography>
                {textField("address", "Mailbox address")}
                {textField("password", "Password", { type: "password" })}
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          loading={isSaving}
          disabled={isSaving}
        >
          {isEdit ? "Save changes" : "Add ingestion"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailIngestionDialog;
